import type { Locale } from "./types";

export type SeoRouteKey =
  | "home"
  | "services"
  | "accounting-agent"
  | "data-rooms"
  | "reports"
  | "pricing"
  | "estimate"
  | "faq"
  | "login";

export type JsonLdIntent = {
  organization: boolean;
  professionalService: boolean;
  softwareApplication: boolean;
  breadcrumbName: string;
  serviceType: string;
  areaServed: "Sweden";
  offerMode: "non_binding_estimate" | "public_software_subscription";
};

export type SeoCatalogEntry = {
  locale: Locale;
  key: SeoRouteKey;
  title: string;
  description: string;
  canonicalPath: "/" | `/#${string}` | "/en" | `/en#${string}`;
  hreflangPartnerPath: "/" | `/#${string}` | "/en" | `/en#${string}`;
  keywords: string[];
  faqSchemaSourceReferences: string[];
  jsonLdIntent: JsonLdIntent;
};

const swedishFaqSourceReferences = [
  "faq-dynamicbooks",
  "faq-estimate",
  "faq-vat",
  "faq-bank-sources",
  "faq-sie",
  "faq-billing-handoff",
  "faq-agent-credits",
  "faq-human-review",
  "faq-data-rooms"
];

const englishFaqSourceReferences = [
  "faq-dynamicbooks",
  "faq-estimate",
  "faq-vat",
  "faq-bank-sources",
  "faq-sie",
  "faq-billing-handoff",
  "faq-agent-credits",
  "faq-human-review",
  "faq-data-rooms"
];

function jsonLdIntent(
  breadcrumbName: string,
  serviceType: string,
  offerMode: JsonLdIntent["offerMode"] = "non_binding_estimate"
): JsonLdIntent {
  return {
    organization: true,
    professionalService: true,
    softwareApplication: true,
    breadcrumbName,
    serviceType,
    areaServed: "Sweden",
    offerMode
  };
}

export const svSeoCatalog = {
  home: {
    locale: "sv",
    key: "home",
    title: "DynamicBooks - agentdriven bokföring, moms och rapporter",
    description:
      "Begär en icke-bindande serviceuppskattning för bokföring, momsstöd, bankmatchning, SIE-migrering, rapporter, Data Rooms och förväntad Agent Credit-kapacitet med mänsklig granskning.",
    canonicalPath: "/",
    hreflangPartnerPath: "/en",
    keywords: [
      "bokföringstjänst",
      "bokföring online",
      "AI bokföring",
      "bokföringsagent",
      "momsrapport",
      "SIE import",
      "BAS kontoplan",
      "banktransaktioner bokföring",
      "reskontra",
      "bokslutsstöd",
      "redovisningstjänst",
      "bokföring för aktiebolag",
      "bokföring för enskild firma"
    ],
    faqSchemaSourceReferences: swedishFaqSourceReferences,
    jsonLdIntent: jsonLdIntent("DynamicBooks", "Bokföring, momsstöd, bankmatchning, SIE-migrering och agentassisterade rapportflöden")
  },
  services: {
    locale: "sv",
    key: "services",
    title: "Bokföringstjänster med DynamicBooks",
    description:
      "Utforska löpande bokföring, bankmatchning, momsstöd och migrering med agentassisterade arbetsflöden, mänsklig granskning och icke-bindande uppskattning.",
    canonicalPath: "/#services",
    hreflangPartnerPath: "/en#services",
    keywords: ["bokföringstjänster", "löpande bokföring", "momsstöd", "bankmatchning", "SIE-migrering"],
    faqSchemaSourceReferences: ["faq-estimate", "faq-vat", "faq-bank-sources", "faq-sie"],
    jsonLdIntent: jsonLdIntent("Tjänster", "Löpande bokföring, bankmatchning, momsstöd och migrering")
  },
  "accounting-agent": {
    locale: "sv",
    key: "accounting-agent",
    title: "Bokföringsagent för DynamicMail Data Rooms",
    description:
      "DynamicBooks använder bokföringsagenter i DynamicMail Data Rooms för torrkörningar, spårbara förslag och kontrollerad redovisningsleverans.",
    canonicalPath: "/#accounting-agent",
    hreflangPartnerPath: "/en#accounting-agent",
    keywords: ["bokföringsagent", "AI bokföring", "Data Rooms", "Agent Credits", "omfattningskontroll"],
    faqSchemaSourceReferences: ["faq-agent-credits", "faq-human-review", "faq-data-rooms"],
    jsonLdIntent: jsonLdIntent("Bokföringsagent", "Agentdrivet redovisningsflöde")
  },
  "data-rooms": {
    locale: "sv",
    key: "data-rooms",
    title: "Redovisning i DynamicMail Data Rooms",
    description:
      "Data Rooms samlar underlag, torrkörningar, rapportartefakter, omfattningssignaler och överlämning till DynamicMail för fortsatt bokföringsleverans.",
    canonicalPath: "/#data-rooms",
    hreflangPartnerPath: "/en#data-rooms",
    keywords: ["Data Rooms", "redovisningsunderlag", "agentdriven bokföring", "rapportartefakter", "omfattningskontroll"],
    faqSchemaSourceReferences: ["faq-data-rooms", "faq-agent-credits", "faq-human-review"],
    jsonLdIntent: jsonLdIntent("Data Rooms", "Data-room-baserad redovisningsleverans")
  },
  reports: {
    locale: "sv",
    key: "reports",
    title: "Moms, balans, resultat och huvudbok i DynamicBooks",
    description:
      "DynamicBooks strukturerar momsrapport, balansrapport, resultatrapport, huvudbok, verifikationslista, kontosaldon och rapportregister.",
    canonicalPath: "/#reports",
    hreflangPartnerPath: "/en#reports",
    keywords: ["momsrapport", "balansrapport", "resultatrapport", "huvudbok", "verifikationslista", "rapportregister"],
    faqSchemaSourceReferences: ["faq-vat", "faq-sie", "faq-data-rooms"],
    jsonLdIntent: jsonLdIntent("Rapporter", "Rapporter och spårbara bokföringsunderlag")
  },
  pricing: {
    locale: "sv",
    key: "pricing",
    title: "DynamicBooks programvarupriser - Basic, Business och Premium",
    description:
      "Se DynamicBooks publika programvarupriser: Basic 149, Business 249 och Premium 399 SEK per månad exklusive moms, separat från serviceuppskattningar.",
    canonicalPath: "/#software-pricing",
    hreflangPartnerPath: "/en#software-pricing",
    keywords: ["bokföringsprogram pris", "programvaruabonnemang", "Basic Business Premium", "bokföringsprogram Sverige"],
    faqSchemaSourceReferences: ["faq-estimate", "faq-billing-handoff", "faq-agent-credits"],
    jsonLdIntent: jsonLdIntent("Programvarupriser", "Bokföringsprogram med publika abonnemangspriser", "public_software_subscription")
  },
  estimate: {
    locale: "sv",
    key: "estimate",
    title: "Serviceuppskattning för bokföring med DynamicBooks",
    description:
      "Få en preliminär och icke-bindande uppskattning för bokföring, moms, rapporter, Agent Credits, abonnemang och omfattningskontroll.",
    canonicalPath: "/#estimate",
    hreflangPartnerPath: "/en#estimate",
    keywords: ["serviceuppskattning", "bokföring pris", "Agent Credits", "bokföring offert", "redovisningstjänst"],
    faqSchemaSourceReferences: ["faq-estimate", "faq-billing-handoff", "faq-agent-credits"],
    jsonLdIntent: jsonLdIntent("Uppskattning", "Icke-bindande serviceuppskattning")
  },
  faq: {
    locale: "sv",
    key: "faq",
    title: "Vanliga frågor om DynamicBooks",
    description:
      "Svar om DynamicBooks, momsrapport, SIE, bankkällor, Agent Credits, Data Rooms, fakturering och omfattningskontroll.",
    canonicalPath: "/#faq",
    hreflangPartnerPath: "/en#faq",
    keywords: ["DynamicBooks frågor", "momsrapport", "SIE import", "Agent Credits", "Data Rooms", "bokföringstjänst"],
    faqSchemaSourceReferences: swedishFaqSourceReferences,
    jsonLdIntent: jsonLdIntent("FAQ", "Frågor och svar om DynamicBooks")
  },
  login: {
    locale: "sv",
    key: "login",
    title: "Logga in i DynamicMail för DynamicBooks",
    description:
      "DynamicBooks leder kunder vidare till DynamicMail för inloggning, Data Rooms, fakturering, abonnemangshantering och fortsatt leverans.",
    canonicalPath: "/#login-handoff",
    hreflangPartnerPath: "/en#login-handoff",
    keywords: ["DynamicMail inloggning", "DynamicBooks inloggning", "Data Rooms", "abonnemangshantering"],
    faqSchemaSourceReferences: ["faq-billing-handoff", "faq-data-rooms"],
    jsonLdIntent: jsonLdIntent("Logga in", "Extern DynamicMail-handoff")
  }
} satisfies Record<SeoRouteKey, SeoCatalogEntry>;

export const enSeoCatalog = {
  home: {
    locale: "en",
    key: "home",
    title: "DynamicBooks - agent-driven accounting, VAT and reports",
    description:
      "Request a non-binding service estimate for bookkeeping, VAT support, bank matching, SIE migration, reports, Data Rooms, and expected Agent Credit capacity with human review.",
    canonicalPath: "/en",
    hreflangPartnerPath: "/",
    keywords: [
      "accounting service Sweden",
      "online bookkeeping Sweden",
      "AI bookkeeping",
      "accounting agent",
      "VAT report",
      "SIE import",
      "BAS chart of accounts",
      "bank transaction accounting",
      "AP AR ledgers",
      "year-end accounting support",
      "bookkeeping for limited companies",
      "bookkeeping for sole traders"
    ],
    faqSchemaSourceReferences: englishFaqSourceReferences,
    jsonLdIntent: jsonLdIntent("DynamicBooks", "Bookkeeping, VAT support, bank matching, SIE migration, and agent-assisted reporting workflows")
  },
  services: {
    locale: "en",
    key: "services",
    title: "Accounting services with DynamicBooks",
    description:
      "Explore monthly bookkeeping, bank matching, VAT support, and migration with agent-assisted workflows, human review, and non-binding service estimates.",
    canonicalPath: "/en#services",
    hreflangPartnerPath: "/#services",
    keywords: ["accounting services", "monthly bookkeeping", "VAT support", "bank matching", "SIE migration"],
    faqSchemaSourceReferences: ["faq-estimate", "faq-vat", "faq-bank-sources", "faq-sie"],
    jsonLdIntent: jsonLdIntent("Services", "Monthly bookkeeping, bank matching, VAT support, and migration workflows")
  },
  "accounting-agent": {
    locale: "en",
    key: "accounting-agent",
    title: "Accounting agent for DynamicMail Data Rooms",
    description:
      "DynamicBooks positions accounting agents for DynamicMail Data Rooms with dry-runs, traceable suggestions, exception flags, and controlled service delivery.",
    canonicalPath: "/en#accounting-agent",
    hreflangPartnerPath: "/#accounting-agent",
    keywords: ["accounting agent", "AI bookkeeping", "Data Rooms", "Agent Credits", "onboarding scope"],
    faqSchemaSourceReferences: ["faq-agent-credits", "faq-human-review", "faq-data-rooms"],
    jsonLdIntent: jsonLdIntent("Accounting Agent", "Agent-driven accounting workflow")
  },
  "data-rooms": {
    locale: "en",
    key: "data-rooms",
    title: "Accounting delivery in DynamicMail Data Rooms",
    description:
      "Data Rooms collect evidence, dry-runs, report artifacts, scope control signals, and the handoff into DynamicMail for continued accounting delivery.",
    canonicalPath: "/en#data-rooms",
    hreflangPartnerPath: "/#data-rooms",
    keywords: ["Data Rooms", "accounting evidence", "agent-driven bookkeeping", "report artifacts", "onboarding scope"],
    faqSchemaSourceReferences: ["faq-data-rooms", "faq-agent-credits", "faq-human-review"],
    jsonLdIntent: jsonLdIntent("Data Rooms", "Data-room-based accounting delivery")
  },
  reports: {
    locale: "en",
    key: "reports",
    title: "VAT, balance sheet, profit and loss, and general ledger in DynamicBooks",
    description:
      "DynamicBooks structures VAT reports, balance sheet, profit and loss, general ledger, journal list, account balances, and the reports register.",
    canonicalPath: "/en#reports",
    hreflangPartnerPath: "/#reports",
    keywords: ["VAT report", "balance sheet", "profit and loss", "general ledger", "journal list", "reports register"],
    faqSchemaSourceReferences: ["faq-vat", "faq-sie", "faq-data-rooms"],
    jsonLdIntent: jsonLdIntent("Reports", "Reports and traceable accounting evidence")
  },
  pricing: {
    locale: "en",
    key: "pricing",
    title: "DynamicBooks software pricing - Basic, Business, and Premium",
    description:
      "View DynamicBooks public software pricing: Basic 149, Business 249, and Premium 399 SEK per month excluding VAT, separate from service estimates.",
    canonicalPath: "/en#software-pricing",
    hreflangPartnerPath: "/#software-pricing",
    keywords: ["accounting software pricing", "software subscription", "Basic Business Premium", "accounting software Sweden"],
    faqSchemaSourceReferences: ["faq-estimate", "faq-billing-handoff", "faq-agent-credits"],
    jsonLdIntent: jsonLdIntent("Software pricing", "Accounting software with public subscription pricing", "public_software_subscription")
  },
  estimate: {
    locale: "en",
    key: "estimate",
    title: "Service estimate for accounting with DynamicBooks",
    description:
      "Get a preliminary, non-binding estimate for bookkeeping, VAT, reports, Agent Credits, subscription access, and onboarding scope.",
    canonicalPath: "/en#estimate",
    hreflangPartnerPath: "/#estimate",
    keywords: ["service estimate", "bookkeeping quote", "Agent Credits", "accounting estimate", "accounting service"],
    faqSchemaSourceReferences: ["faq-estimate", "faq-billing-handoff", "faq-agent-credits"],
    jsonLdIntent: jsonLdIntent("Estimate", "Non-binding service estimate")
  },
  faq: {
    locale: "en",
    key: "faq",
    title: "DynamicBooks FAQ for accounting services",
    description:
      "Answers about DynamicBooks, VAT reports, SIE, bank sources, Agent Credits, Data Rooms, billing handoff, and onboarding scope.",
    canonicalPath: "/en#faq",
    hreflangPartnerPath: "/#faq",
    keywords: ["DynamicBooks FAQ", "VAT report", "SIE import", "Agent Credits", "Data Rooms", "accounting service"],
    faqSchemaSourceReferences: englishFaqSourceReferences,
    jsonLdIntent: jsonLdIntent("FAQ", "Questions and answers about DynamicBooks")
  },
  login: {
    locale: "en",
    key: "login",
    title: "Log in to DynamicMail for DynamicBooks",
    description:
      "DynamicBooks hands customers off to DynamicMail for login, Data Rooms, billing, subscription management, and continued delivery.",
    canonicalPath: "/en#login-handoff",
    hreflangPartnerPath: "/#login-handoff",
    keywords: ["DynamicMail login", "DynamicBooks login", "Data Rooms", "subscription management"],
    faqSchemaSourceReferences: ["faq-billing-handoff", "faq-data-rooms"],
    jsonLdIntent: jsonLdIntent("Log in", "External DynamicMail handoff")
  }
} satisfies Record<SeoRouteKey, SeoCatalogEntry>;

export const seoCatalog = {
  sv: svSeoCatalog,
  en: enSeoCatalog
} satisfies Record<Locale, Record<SeoRouteKey, SeoCatalogEntry>>;
